<template>
  <ons-page id="search">
    <ons-toolbar>
      <div class="left">
        <ons-back-button>{{ $t("ui.back") }}</ons-back-button>
      </div>
      <div class="center">
        <v-ons-search-input :placeholder="$t('search.search')" modifier="full" value="" autofocus v-model="keywords" :disabled="isOffline" @input="debouncedSearch"></v-ons-search-input>
        <button class="flat-button js-search-filter-toggle" modifier="quiet small"
          :disabled="isOffline"
          @click="openSortActionSheet()"
        ><icon-side-menu /></button>
      </div>
    </ons-toolbar>
    <v-ons-progress-bar indeterminate class="app-bar-progress" v-show="loading"></v-ons-progress-bar>
    <top-pull-hook :action="reload" />
    <h1 class="page-title">{{ $t("search.title") }}</h1>
    <div class="page-info">
      <div class="small" v-if="isOffline">{{ $t("search.offline") }}</div>
      <div class="small" v-else-if="loading">{{ $t("search.loading", { keyword: lastSearchKeywords }) }}</div>
      <div class="small" v-else-if="!searchData">{{ $t("search.start_search") }}</div>
      <div class="small" v-else>{{ $t("search.search_result", {
        channel_count: `${searchData.channels.length}${(searchData.channels.length === 0 || searchData._channelsEnded) ? '' : '+'}`,
        episode_count: `${searchData.episodes.length}${(searchData.episodes.length === 0 || searchData._episodesEnded) ? '' : '+'}`,
        tag_count: `${searchData.tags.length}${(searchData.tags.length === 0 || searchData._tagsEnded) ? '' : '+'}`,
        keyword: lastSearchKeywords
      }) }}</div>
    </div>
    <div class="tabs tabbar tabbar--top" data-control="#tabs-content-search" v-if="!isOffline">
      <label class="tabbar__item" data-index="1">
      <input type="radio" name="tab-search" checked="checked" @click="setTabIndex(0)">
      <button class="tabbar__button">
        {{ $t("tag.nav.channels") }}
      </button>
      </label>
      <label class="tabbar__item" data-index="2">
      <input type="radio" name="tab-search" @click="setTabIndex(1)">
      <button class="tabbar__button">
        {{ $t("tag.nav.episodes") }}
      </button>
      </label>
      <label class="tabbar__item" data-index="3">
      <input type="radio" name="tab-search" @click="setTabIndex(2)">
      <button class="tabbar__button">
        {{ $t("tag.nav.tags") }}
      </button>
      </label>
    </div>
    <div class="tabs-content" id="tabs-content-search">
      <div v-show="tabIndex === 0">
        <div class="pd-bottom container">
          <channel-list-detailed :channels="searchData && searchData.channels" :on-toggle-subscribe-start="onToggleSubscribeStart" :on-toggle-subscribe-error="onToggleSubscribeError" :on-toggle-subscribe-end="onToggleSubscribeEnd" />
        </div>
        <div class="center-align" v-show="!!searchData">
          <p>
            <v-ons-button class="btn-outline" modifier="long"
              v-show="searchData && !searchData._channelsEnded"
              :disabled="loading"
              @click="loadMore('channels')"
            >{{ $t("ui.load_more") }}</v-ons-button>
          </p>
          <p v-show="searchData && searchData._channelsEnded">{{ $t("ui.no_more") }}</p>
        </div>
      </div>
      <div v-show="tabIndex === 1">
        <div class="pd-bottom container">
          <episode-list-detailed :episodes="searchData && searchData.episodes" />
        </div>
        <div class="center-align" v-show="!!searchData">
          <p>
            <v-ons-button class="btn-outline" modifier="long"
              v-show="searchData && !searchData._episodesEnded"
              :disabled="loading"
              @click="loadMore('episodes')"
            >{{ $t("ui.load_more") }}</v-ons-button>
          </p>
          <p v-show="searchData && searchData._episodesEnded">{{ $t("ui.no_more") }}</p>
        </div>
      </div>
      <div v-show="tabIndex === 2">
        <tag-list :tags="searchData && searchData.tags" />
        <div class="center-align">
          <p>
            <v-ons-button class="btn-outline" modifier="long"
              v-show="searchData && !searchData._tagsEnded"
              :disabled="loading"
              @click="loadMore('tags')"
            >{{ $t("ui.load_more") }}</v-ons-button>
          </p>
          <p v-show="searchData && searchData._tagsEnded">{{ $t("ui.no_more") }}</p>
        </div>
      </div>
    </div>
    <v-ons-toast :visible="shouldShowGoOnlineToast" animation="ascend">
      {{ $t("messages.go_online_to_search") }}
      <button @click="goOnline">{{ $t("ui.go_online") }}</button>
    </v-ons-toast>
    <float-player class="player-iphonex-apply-bottom-notch" />
  </ons-page>
</template>

<script>
import { debounce } from "debounce";
import TopPullHook from '@/components/TopPullHook.vue';
import ChannelListDetailed from '@/components/ChannelListDetailed.vue';
import EpisodeListDetailed from '@/components/EpisodeListDetailed.vue';
import TagList from '@/components/TagList.vue';
import FloatPlayer from '@/components/FloatPlayer.vue';
import Search from '@/models/Search';
import TabHelper from '@/mixins/TabHelper';
import WatchUserChangeHelper from '@/mixins/WatchUserChangeHelper';

export default {
  name: 'search',
  components: {
    'top-pull-hook': TopPullHook,
    'channel-list-detailed': ChannelListDetailed,
    'episode-list-detailed': EpisodeListDetailed,
    'tag-list': TagList,
    'float-player': FloatPlayer,
  },
  mixins: [TabHelper, WatchUserChangeHelper],

  data() {
    return {
      keywords: null,
      lastSearchKeywords: null,
      orderBy: 'relevancy',
      loading: false,
    };
  },

  computed: {
    keywordsIsValid() {
      return this.keywords && this.keywords.replace(/[&\/\\#,+()$~%.'":*?<>{}@ ]/g, '').length >= 2;
    },
    searchData() {
      return Search.query().with('channels.episodes').with('episodes.channel').with('tags.channels').first();
    },
  },

  methods: {
    reload(done) {
      if (!this.keywordsIsValid) { return; }
      let that = this;

      this.loading = true;
      this.lastSearchKeywords = this.keywords;
      Search.fetch({ keywords: this.lastSearchKeywords, orderBy: this.orderBy })
        .catch((error) => {
          if (error.response && error.response.status === 401) {
            that.navigatorResetLogin(that.$t("messages.login_required"));
          } else {
            console.log(error);
            that.showSomethingWrongToast();
          }
        }).finally(() => {
          this.loading = false;
          if (done) { done(); }
        });
    },
    search() {
      this.reload();
    },
    onToggleSubscribeStart() {
      this.loading = true;
    },
    onToggleSubscribeError(error) {
      console.log(error);
      this.showSomethingWrongToast();
    },
    onToggleSubscribeEnd() {
      this.loading = false;
    },
    loadMore(type) {
      let that = this;
      this.loading = true;
      this.searchData.fetchMore({ keywords: this.lastSearchKeywords, type: type, orderBy: this.orderBy })
        .catch(function (error) {
          console.log(error);
          that.showSomethingWrongToast();
        }).finally(() => {
          this.loading = false;
        });
    },
    openSortActionSheet() {
      let that = this;
      this.$ons.openActionSheet({
        title: this.$t("search.sort.title"),
        cancelable: true,
        buttons: [
          this.$t("search.sort.by_relevance"),
          this.$t("search.sort.by_popularity"),
          this.$t("search.sort.by_date"),
          this.$t("ui.cancel"),
        ],
      }).then(function (index) {
        if (index === 0) {
          that.orderBy = 'relevancy';
          that.reload();
        }
        if (index === 1) {
          that.orderBy = 'popularity';
          that.reload();
        }
        if (index === 2) {
          that.orderBy = 'published_at';
          that.reload();
        }
      });
    },
    onUserChanged() {
      this.search();
    },
  },

  created() {
    this.debouncedSearch = debounce(() => {
      this.search();
    }, 1000);
  },

  mounted() {
    let input = this.$el.querySelector('[autofocus]');
    if (input) { input.focus(); }
  },
};
</script>
